import { useState } from "react";
import "./Payment.scss";
import logo from "../../assets/logo.png";
import { RAZORPAY_CONFIG } from "../config/razorpayConfig";

function Payment(props) {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success' | 'error' | null

  const presetAmounts = [100, 250, 500, 1000, 2500];
  const MIN_DONATION_AMOUNT = 1;

  const closePaymentModal = () => {
    props.closePaymentQrValue(true);
  };

  const handlePresetAmountClick = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount("");
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setCustomAmount(value);
      setSelectedAmount(null);
    }
  };

  const getDonationAmount = () => {
    if (selectedAmount) return selectedAmount;
    if (customAmount) return parseInt(customAmount, 10);
    return 0;
  };

  const initiatePayment = async () => {
    const donationAmount = getDonationAmount();

    if (!donationAmount || donationAmount < MIN_DONATION_AMOUNT) {
      alert(
        `Please select or enter a minimum amount of ₹${MIN_DONATION_AMOUNT}`,
      );
      return;
    }

    setIsProcessing(true);
    setPaymentStatus(null);

    try {
      // Step 1: Create an order on the server
      const response = await fetch("/api/create-order", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ amount: donationAmount }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create order");
      }

      // Step 2: Open Razorpay Checkout
      const options = {
        key: RAZORPAY_CONFIG.keyId,
        amount: donationAmount * 100, // paise
        currency: "INR",
        name: RAZORPAY_CONFIG.name,
        description: `One-time donation of ₹${donationAmount} to ISC Foundation`,
        order_id: data.order.id,
        handler: function (response) {
          // Payment successful
          console.log("Payment successful:", response);
          setPaymentStatus("success");
          setIsProcessing(false);

          // Auto-close modal after 3 seconds
          setTimeout(() => {
            closePaymentModal();
          }, 3000);
        },
        prefill: {
          name: "",
          email: "",
          contact: "",
        },
        notes: {
          donation_type: "one_time",
          amount_inr: donationAmount,
        },
        theme: {
          color: RAZORPAY_CONFIG.theme.color,
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        console.error("Payment failed:", response.error);
        setPaymentStatus("error");
        setIsProcessing(false);
      });
      rzp.open();
    } catch (error) {
      console.error("Payment initiation error:", error);
      alert(`Error: ${error.message}. Please try again.`);
      setIsProcessing(false);
    }
  };

  return (
    <div className="payment-model">
      <div className="payment-model__header">
        <img src={logo} alt="ISC Foundation logo" loading="lazy" />
        <p>ISC Foundation</p>
      </div>

      <h2>
        Donate Now<span>!</span>
      </h2>
      <p className="payment-model__subtitle">
        Make a one-time contribution of any amount
      </p>

      {/* Success Message */}
      {paymentStatus === "success" && (
        <div className="payment-status success">
          <i className="fa fa-check-circle" aria-hidden="true"></i>
          <p>Thank you for your generous donation!</p>
          <span>Your payment was successful.</span>
        </div>
      )}

      {/* Error Message */}
      {paymentStatus === "error" && (
        <div className="payment-status error">
          <i className="fa fa-times-circle" aria-hidden="true"></i>
          <p>Payment failed</p>
          <span>Please try again or use a different payment method.</span>
        </div>
      )}

      {/* Payment Form — hidden on success */}
      {paymentStatus !== "success" && (
        <div className="payment-form">
          {/* Preset Amount Buttons */}
          <div className="form-group">
            <label>Select Amount (₹)</label>
            <div className="preset-amounts">
              {presetAmounts.map((amount) => (
                <button
                  type="button"
                  key={amount}
                  className={`preset-amount-btn ${selectedAmount === amount ? "active" : ""}`}
                  data-selected={selectedAmount === amount}
                  aria-pressed={selectedAmount === amount}
                  onClick={() => handlePresetAmountClick(amount)}
                  disabled={isProcessing}
                >
                  ₹{amount.toLocaleString("en-IN")}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Amount Input */}
          <div className="form-group">
            <label htmlFor="custom-donation-amount">
              Or Enter Custom Amount (₹)
            </label>
            <input
              type="text"
              id="custom-donation-amount"
              className="amount-input"
              value={customAmount}
              onChange={handleCustomAmountChange}
              placeholder={`Min ₹${MIN_DONATION_AMOUNT}`}
              disabled={isProcessing}
            />
          </div>

          {/* Donation Preview */}
          {getDonationAmount() >= MIN_DONATION_AMOUNT && (
            <div className="donation-preview">
              <div className="preview-row">
                <span>Donation Amount:</span>
                <strong>₹{getDonationAmount().toLocaleString("en-IN")}</strong>
              </div>
              <div className="preview-row">
                <span>Type:</span>
                <strong>One-time</strong>
              </div>
            </div>
          )}

          {/* Pay Now Button */}
          <button
            type="button"
            className="pay-now-btn"
            onClick={initiatePayment}
            disabled={isProcessing || getDonationAmount() < MIN_DONATION_AMOUNT}
          >
            {isProcessing ? (
              <span className="btn-loading">
                <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                &nbsp;Processing...
              </span>
            ) : getDonationAmount() >= MIN_DONATION_AMOUNT ? (
              `Pay ₹${getDonationAmount().toLocaleString("en-IN")}`
            ) : (
              "Select an Amount"
            )}
          </button>

          {/* Secure Badge */}
          <div className="secure-badge">
            <i className="fa fa-lock" aria-hidden="true"></i>
            <span>Secured by Razorpay</span>
          </div>
        </div>
      )}

      {/* Close Button */}
      <div className="payment-model__close">
        <button type="button" onClick={closePaymentModal} disabled={isProcessing}>
          {paymentStatus === "success" ? "Done" : "Close"}
        </button>
      </div>
    </div>
  );
}

export default Payment;